import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Landmark, Scale, BadgeCheck, ArrowRight } from "lucide-react";

/**
 * Phase 11 — prominent compliance trust band. Raises regulated-industry
 * credibility above the fold-adjacent area so prospects see licensing +
 * oversight before they decide to submit personal info.
 */

const BADGES = [
  {
    id: "licensed",
    Icon: Landmark,
    title: "Licensed insurance professionals",
    body: "Insurance and annuity products sold through state-licensed producers. We confirm your state availability before any application.",
  },
  {
    id: "naic",
    Icon: Scale,
    title: "NAIC-aligned practices",
    body: "We follow NAIC Model Regulations for suitability, replacement, and advertising. Every recommendation is fit-for-purpose, not fit-for-commission.",
  },
  {
    id: "carriers",
    Icon: ShieldCheck,
    title: "Multi-carrier independent",
    body: "No single-carrier captive deals. Guarantees are backed by the financial strength and claims-paying ability of the issuing carrier.",
  },
  {
    id: "no-advice",
    Icon: BadgeCheck,
    title: "Educational posture",
    body: "We do not provide tax, legal, or registered investment advice. We coordinate with your CPA and attorney when a strategy requires it.",
  },
];

export const ComplianceTrustStrip = () => {
  return (
    <section
      id="trust"
      data-testid="compliance-trust-strip"
      className="py-20 sm:py-24 bg-white relative"
    >
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 gold-hairline opacity-80"
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-4">
            <span className="lw-eyebrow">Regulated. Transparent. Independent.</span>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-[1.1]">
              Trust begins with <span className="italic">disclosure.</span>
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-lw-muted">
              Life insurance, annuities, long-term care, and health products
              are among the most regulated instruments in American personal
              finance. Here's how we work inside that framework — and where
              you can see the details.
            </p>
            <Link
              to="/compliance"
              data-testid="compliance-trust-full-link"
              className="btn-ghost-navy mt-6 text-sm"
            >
              Read full disclosures <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4 sm:gap-5">
            {BADGES.map(({ id, Icon, title, body }) => (
              <div
                key={id}
                data-testid={`compliance-badge-${id}`}
                className="rounded-xl border border-lw-navy/10 bg-lw-ivory-warm/60 p-5"
              >
                <div className="w-10 h-10 rounded-lg bg-lw-navy text-lw-gold flex items-center justify-center">
                  <Icon size={18} />
                </div>
                <h3 className="mt-4 font-serif-display text-base text-lw-navy leading-snug">
                  {title}
                </h3>
                <p className="mt-1.5 text-xs leading-relaxed text-lw-muted">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComplianceTrustStrip;
